import LinkField from 'components/LinkField';
import React from 'react';
import {
  TextField,
  Datagrid,
  ReferenceManyField,
  SimpleShowLayout,
  FunctionField,
  Pagination,
  useListContext,
  useRecordContext,
  useNotify,
} from 'react-admin';
import { formatDate } from 'utils/date';
import { CircularProgress, Button } from '@mui/material';
import GetApp from '@mui/icons-material/GetApp';
import { ProfessionalQualification } from 'typings/graphql';
import useGetPDF from 'hooks/useGetPDF';

type SkillsDownloadButtonProps = {
  isDownloading: boolean;
  handleDownloadAllSkills: (skillsAssessments: ProfessionalQualification[]) => void;
};

const SkillsDownloadButton = ({
  isDownloading,
  handleDownloadAllSkills,
}: SkillsDownloadButtonProps) => {
  const { data } = useListContext<ProfessionalQualification>();

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={isDownloading ? <CircularProgress size={22} /> : <GetApp />}
      onClick={() => handleDownloadAllSkills(data)}
      disabled={isDownloading}
      sx={{ width: 205 }}
    >
      {isDownloading ? `Downloading` : 'Skills Assessments'}
    </Button>
  );
};

const SkillsLayout = ({ isDownloading, handleDownloadAllSkills }: SkillsDownloadButtonProps) => (
  <SimpleShowLayout>
    <ReferenceManyField
      label={false}
      pagination={<Pagination />}
      reference="ProfessionalQualification"
      filter={{ search: { qualification_types: ['typeform', 'external_skills_assessment'] } }}
      target="professional_id"
    >
      <SkillsDownloadButton
        isDownloading={isDownloading}
        handleDownloadAllSkills={handleDownloadAllSkills}
      />
      <Datagrid bulkActionButtons={false} data-testid="professional_show-skills_tab_table">
        <LinkField resource="ProfessionalQualification" source="id" />
        <TextField
          label="Skills Assessment"
          source="qualification.long_name"
          data-testid="professional_show-skills_tab_skills_assessment"
        />
        <FunctionField
          label="Expiration Date"
          render={(v) =>
            formatDate(
              v?.professional_qualification_attributes?.find(({ type }) =>
                type.includes('ExpirationDate'),
              )?.value,
            )
          }
          data-testid="professional_show-skills_tab_expiration_date"
        />
        <FunctionField
          label="Date Taken"
          render={(v) => {
            const dateTaken = v?.professional_qualification_attributes?.find(
              (pqa) => pqa.qualification_attribute?.name === 'Date Taken',
            );
            return formatDate(dateTaken?.value);
          }}
        />
        <FunctionField
          label="Created At Date"
          render={(v) => formatDate(v?.created_at)}
          data-testid="professional_show-skills_tab_created_at_date"
        />
      </Datagrid>
    </ReferenceManyField>
  </SimpleShowLayout>
);

const SkillsTab = () => {
  const getPDF = useGetPDF();
  const notify = useNotify();

  const [isDownloading, setIsDownloading] = React.useState(false);
  const professional = useRecordContext();

  const handleDownloadAllSkills = async (skillsAssessments: ProfessionalQualification[]) => {
    const fullName = professional?.account?.name ?? 'Unknown';

    skillsAssessments.forEach(async (assessment) => {
      const fileName = `${fullName}_${assessment.qualification.long_name}.pdf`;

      try {
        setIsDownloading(true);
        await getPDF({ url: `/professional_qualifications/${assessment.id}` }, fileName);
      } catch (error) {
        notify(`Failed to download skills assessment: ${assessment.qualification.long_name}`, {
          type: 'error',
        });
      } finally {
        setIsDownloading(false);
      }
    });
  };

  return (
    <SkillsLayout isDownloading={isDownloading} handleDownloadAllSkills={handleDownloadAllSkills} />
  );
};

export default SkillsTab;
